import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, SearchIcon, PackageIcon, ArrowRightIcon, ClockIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Order } from '../admin/types';
import { StatusBadge } from '../admin/StatusBadge';
import { CONFIG } from '../data/config';
interface OrderLookupProps {
  onBack: () => void;
  onTrackOrder: (token: string) => void;
}
export function OrderLookup({
  onBack,
  onTrackOrder
}: OrderLookupProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [foundOrder, setFoundOrder] = useState<Order | null>(null);
  const [recentOrder, setRecentOrder] = useState<{
    orderNumber: string;
    trackingToken: string;
    timestamp: number;
  } | null>(null);
  useEffect(() => {
    try {
      const saved = localStorage.getItem('recent_order');
      if (saved) {
        const parsed = JSON.parse(saved);
        const elapsed = Date.now() - parsed.timestamp;
        const TEN_MINUTES = 10 * 60 * 1000;
        if (elapsed < TEN_MINUTES) {
          setRecentOrder(parsed);
        }
      }
    } catch (e) {}
  }, []);
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    setLoading(true);
    setError(null);
    setFoundOrder(null);
    try {
      const {
        data,
        error: fetchError
      } = await supabase.from('orders').select('*').ilike('order_number', `%${query}%`).single();
      if (fetchError || !data) {
        setError('No encontramos un pedido con ese número. Verifica e intenta de nuevo.');
        return;
      }
      const order = data as Order;
      if (order.status === 'delivered' || order.status === 'cancelled') {
        setError('Este pedido ya fue entregado o cancelado. Solo puedes rastrear pedidos activos.');
        return;
      }
      setFoundOrder(order);
    } catch (err) {
      setError('Ocurrió un error al buscar el pedido. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 50
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: 50
  }} className="fixed inset-0 z-40 bg-carbon flex flex-col max-w-md mx-auto shadow-2xl" data-id="element-1316">
      <div className="bg-surface px-4 py-4 flex items-center shadow-sm z-10 border-b border-gray-800" data-id="element-1317">
        <button onClick={onBack} className="p-2 -ml-2 text-gray-400 hover:text-white transition-colors" data-id="element-1318">
          <ChevronLeftIcon className="w-6 h-6" data-id="element-1319" />
        </button>
        <h2 className="font-heading font-semibold text-lg ml-2 text-white tracking-wide" data-id="element-1320">
          Estado de mi pedido
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-8" data-id="element-1321">
        {/* Recent Order Quick Access */}
        {recentOrder && <section className="bg-dorado/10 border border-dorado/20 p-5 rounded-2xl" data-id="element-1322">
            <div className="flex items-start gap-3 mb-4" data-id="element-1323">
              <div className="p-2 bg-dorado/20 rounded-xl text-dorado" data-id="element-1324">
                <ClockIcon className="w-5 h-5" data-id="element-1325" />
              </div>
              <div data-id="element-1326">
                <h3 className="font-heading font-semibold text-dorado mb-1" data-id="element-1327">
                  Tu pedido reciente
                </h3>
                <p className="text-sm text-dorado/80" data-id="element-1328">
                  Detectamos que hiciste un pedido hace poco.
                </p>
              </div>
            </div>
            <button onClick={() => onTrackOrder(recentOrder.trackingToken)} className="w-full py-3 bg-dorado text-carbon font-bold rounded-xl text-center text-sm hover:bg-dorado/90 transition-colors flex items-center justify-center gap-2" data-id="element-1329">
              <PackageIcon className="w-4 h-4" data-id="element-1330" />
              Rastrear pedido #{recentOrder.orderNumber}
            </button>
          </section>}

        {/* Search Form */}
        <section data-id="element-1331">
          <h3 className="font-heading font-semibold text-white mb-4 text-lg" data-id="element-1332">
            Buscar por número de pedido
          </h3>
          <form onSubmit={handleSearch} className="space-y-4" data-id="element-1333">
            <div data-id="element-1334">
              <div className="relative" data-id="element-1335">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1336">
                  <SearchIcon className="h-5 w-5 text-gray-500" data-id="element-1337" />
                </div>
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Ej. AV-20260314-230950" className="w-full pl-11 pr-4 py-3.5 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-dorado focus:border-dorado outline-none text-white placeholder-gray-600 font-mono text-sm transition-all" data-id="element-1338" />
              </div>
              <p className="text-xs text-gray-400 mt-3 leading-relaxed" data-id="element-1339">
                Ingresa aquí el número de pedido que aparece en el ticket
                digital o en el mensaje de confirmación que te enviamos por
                WhatsApp 💬
              </p>
            </div>

            <button type="submit" disabled={!searchQuery.trim() || loading} className="w-full py-3.5 bg-surface-light text-white font-semibold rounded-xl border border-gray-700 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1340">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1341" /> : <>
                  Buscar pedido
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1342" />
                </>}
            </button>
          </form>

          {error && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400" data-id="element-1343">
              {error}
            </motion.div>}

          {foundOrder && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mt-6 bg-surface border border-gray-700 rounded-2xl p-5 shadow-xl" data-id="element-1344">
              <div className="flex items-center justify-between mb-4" data-id="element-1345">
                <span className="font-mono text-sm font-bold text-white" data-id="element-1346">
                  #{foundOrder.order_number}
                </span>
                <StatusBadge status={foundOrder.status} size="sm" data-id="element-1347" />
              </div>

              <div className="space-y-2 mb-6" data-id="element-1348">
                <p className="text-sm text-gray-400" data-id="element-1349">
                  <span className="text-gray-500" data-id="element-1350">Cliente:</span>{' '}
                  {foundOrder.customer_name}
                </p>
                <p className="text-sm text-gray-400" data-id="element-1351">
                  <span className="text-gray-500" data-id="element-1352">Total:</span>{' '}
                  {CONFIG.CURRENCY_SYMBOL}
                  {foundOrder.total_final.toLocaleString()}
                </p>
              </div>

              <button onClick={() => onTrackOrder(foundOrder.tracking_token)} className="w-full py-3 bg-verde text-carbon font-bold rounded-xl hover:bg-verde/90 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-verde/20" data-id="element-1353">
                Ver seguimiento en vivo
                <ArrowRightIcon className="w-4 h-4" data-id="element-1354" />
              </button>
            </motion.div>}
        </section>
      </div>
    </motion.div>;
}